// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  concatMap,
  debounceTime,
  delay,
  from,
  map,
  of,
  Subject,
  takeUntil,
} from 'rxjs';
import { LocalizedDatePipe } from 'src/app/modules/shared/pipes/localizedDate.pipe';
import { ScreenLayoutService } from 'src/app/modules/shared/services/screen-layout-service/screen-layout.service';
import { SharedModule } from '../../../shared/shared.module';
import { DnaSyngoviaGroupNode } from '../../interfaces/dna-syngovia-group-node';
import {
  DnaSyngoviaNodeEType,
  DnaSyngoviaNodeType,
} from '../../interfaces/dna-syngovia-node';
import { DnaSyngoviaColumnModel } from '../../model/dna-syngovia-column-model';
import {
  DnaSyngoviaSelectionModel,
  DnaSyngoviaSelectionType,
} from '../../model/dna-syngovia-selection-model';
import { DnaSyngoviaIconService } from '../../services/dna-syngovia-icon.service';
import { DnaSyngoviaScreenInteractionService } from '../../services/dna-syngovia-screen-interaction.service';
import { DnaSyngoviaComparisonBoardComponent } from '../dna-syngovia-comparison-board/dna-syngovia-comparison-board.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SharedModule,
    DnaSyngoviaComparisonBoardComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-dna-syngovia-group-table-list',
  templateUrl: './dna-syngovia-group-table-list.component.html',
  styleUrls: ['./dna-syngovia-group-table-list.component.scss'],
})
export class DnaSyngoviaGroupTableListComponent
  implements OnChanges, OnInit, OnDestroy
{
  @Input({ required: true }) groupNode!: DnaSyngoviaGroupNode;

  @ViewChild('searchInput') searchInput!: ElementRef;

  protected filteredNodes!: DnaSyngoviaSelectionType[];
  protected selectionModel!: DnaSyngoviaSelectionModel;
  protected isTableLoading = false;

  private readonly searchSubject = new Subject<string>();
  private readonly batchSubject = new Subject<{
    slices: { start: number; end: number }[];
    data: DnaSyngoviaSelectionType[];
  }>();

  private readonly unsubscribe = new Subject<void>();

  private readonly BATCH_LENGTH = 25;
  private readonly MAX_ALLOWED_DATA_LENGTH = 10000;

  private readonly defaultSortProperty: { [key: string]: string } = {
    default: 'logicalName',
    client: 'hostname',
    modality: 'modelName',
    server: 'serialNumber',
  };

  protected readonly serverNetworkAdaptersColumns = [
    new DnaSyngoviaColumnModel({
      property: 'ip',
      minWidth: '435px',
      columnSize: '6',
    }),
    new DnaSyngoviaColumnModel({ property: 'dns', minWidth: '250px' }),
    new DnaSyngoviaColumnModel({ property: 'subnet', minWidth: '152px' }),
    new DnaSyngoviaColumnModel({ property: 'gateway', minWidth: '152px' }),
  ];

  protected readonly clientColumns = [
    new DnaSyngoviaColumnModel({
      property: 'hostname',
      minWidth: '435px',
    }),
    new DnaSyngoviaColumnModel({
      property: 'os',
      minWidth: '400px',
      columnSize: '6',
    }),
    new DnaSyngoviaColumnModel({
      property: 'swVersion',
      minWidth: '120px',
      columnSize: '3',
    }),
  ];

  protected readonly clientNetworkAdaptersColumns = [
    new DnaSyngoviaColumnModel({
      property: 'ip',
      minWidth: '479px',
      columnSize: '6',
    }),
    new DnaSyngoviaColumnModel({
      property: 'networkSpeed',
      minWidth: '400px',
      columnSize: '2',
    }),
    new DnaSyngoviaColumnModel({
      property: 'networkAdapter',
      minWidth: '400px',
    }),
  ];

  protected readonly clientVideoControllersColumns = [
    new DnaSyngoviaColumnModel({
      property: 'deviceId',
      minWidth: '479px',
      columnSize: '5',
    }),
    new DnaSyngoviaColumnModel({
      property: 'driverVersion',
      minWidth: '400px',
      columnSize: '3',
    }),
    new DnaSyngoviaColumnModel({
      property: 'driverDate',
      minWidth: '170px',
      columnType: 'date',
      columnSize: '2',
    }),
  ];

  protected readonly clientDisplayControllersColumns = [
    new DnaSyngoviaColumnModel({
      property: 'device',
      minWidth: '523px',
      columnSize: '5',
    }),
    new DnaSyngoviaColumnModel({
      property: 'deviceString',
      minWidth: '400px',
      columnSize: '4',
    }),
    new DnaSyngoviaColumnModel({
      property: 'isPrimary',
      minWidth: '130px',
      columnSize: '2',
    }),
    new DnaSyngoviaColumnModel({
      property: 'monitorResolution',
      minWidth: '193px',
    }),
  ];

  protected readonly nodeDicomServiceColumns = [
    new DnaSyngoviaColumnModel({
      property: 'name',
      minWidth: '180px',
      header: 'service',
    }),
    new DnaSyngoviaColumnModel({
      property: 'aet',
      minWidth: '180px',
      columnSize: '4',
    }),
    new DnaSyngoviaColumnModel({
      property: 'port',
      minWidth: '80px',
      columnSize: '3',
    }),
    new DnaSyngoviaColumnModel({ property: 'storage', minWidth: '180px' }),
  ];

  protected readonly columnsDictionary: {
    [key: string]: DnaSyngoviaColumnModel[];
  } = {
    default: [
      new DnaSyngoviaColumnModel({ property: 'modelName', minWidth: '375px' }),
      new DnaSyngoviaColumnModel({
        property: 'logicalName',
        minWidth: '250px',
      }),
      new DnaSyngoviaColumnModel({ property: 'hostname', minWidth: '300px' }),
      new DnaSyngoviaColumnModel({ property: 'ip', minWidth: '130px' }),
    ],
    client: this.clientColumns,
    modality: [
      new DnaSyngoviaColumnModel({ property: 'modelName', minWidth: '381px' }),
      new DnaSyngoviaColumnModel({
        property: 'logicalName',
        minWidth: '250px',
      }),

      new DnaSyngoviaColumnModel({ property: 'hostname', minWidth: '300px' }),
      new DnaSyngoviaColumnModel({ property: 'ip', minWidth: '130px' }),
    ],
    server: [
      new DnaSyngoviaColumnModel({
        property: 'serialNumber',
        minWidth: '185px',
        header: 'sn',
      }),
      new DnaSyngoviaColumnModel({
        property: 'handover',
        minWidth: '131px',
        columnType: 'date',
      }),
      new DnaSyngoviaColumnModel({ property: 'msl', minWidth: '100px' }),
      new DnaSyngoviaColumnModel({ property: 'swVersion', minWidth: '130px' }),
      new DnaSyngoviaColumnModel({ property: 'aet', minWidth: '133px' }),
      new DnaSyngoviaColumnModel({ property: 'hostname', minWidth: '170px' }),
      new DnaSyngoviaColumnModel({ property: 'virtual', minWidth: '120px' }),
      new DnaSyngoviaColumnModel({ property: 'iloIp', minWidth: '141px' }),
      new DnaSyngoviaColumnModel({ property: 'hpSn', minWidth: '400px' }),
      new DnaSyngoviaColumnModel({ property: 'sku', minWidth: '100px' }),
      new DnaSyngoviaColumnModel({
        property: 'systemModel',
        minWidth: '400px',
        columnSize: '8',
      }),
    ],
  };

  protected modalVisible = false;

  public constructor(
    protected readonly dnaSyngoviaIconService: DnaSyngoviaIconService,
    private readonly localizedDatePipe: LocalizedDatePipe,
    protected readonly screenLayoutService: ScreenLayoutService,
    private readonly screenInteractionService: DnaSyngoviaScreenInteractionService
  ) {
    this.listenToSearchSubject();
    this.listenToBatchProcess();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['groupNode']) {
      this.initFilteredNodes();
    }
  }

  public ngOnInit(): void {
    this.initCustomTableStyles();
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  protected initFilteredNodes(): void {
    this.selectionModel = new DnaSyngoviaSelectionModel(this.groupNode.nodes);

    const defaultSortProperty =
      this.defaultSortProperty[this.groupNode.type] ??
      this.defaultSortProperty.default;

    this.selectionModel.selection.sort(
      this.defaultSortFn(defaultSortProperty, false)
    );

    this.dispatchBatchProcess(this.selectionModel.selection);
  }

  private dispatchBatchProcess(nodes: DnaSyngoviaSelectionType[]) {
    if (nodes.length <= this.BATCH_LENGTH) {
      this.filteredNodes = nodes;
    } else {
      this.batchSubject.next({
        slices: this.getDataSlices(nodes.length),
        data: nodes,
      });
    }
  }

  private getDataSlices(dataLength: number): { start: number; end: number }[] {
    // Validate input to prevent DoS attacks from excessive iterations
    if (
      !Number.isInteger(dataLength) ||
      dataLength < 0 ||
      dataLength > this.MAX_ALLOWED_DATA_LENGTH
    ) {
      return [];
    }
    const safeLength = Math.min(dataLength, this.MAX_ALLOWED_DATA_LENGTH);

    let end = 0;
    const slices: { start: number; end: number }[] = [];
    for (let start = 0; start < safeLength; start += this.BATCH_LENGTH) {
      end = Math.min(start + this.BATCH_LENGTH, safeLength);
      slices.push({ start, end });
    }

    return slices;
  }

  private listenToBatchProcess(): void {
    this.batchSubject
      .pipe(
        concatMap((value) => {
          this.isTableLoading = true;
          this.filteredNodes = value.data.slice(
            value.slices[0].start,
            value.slices[0].end
          );

          return from(value.slices.slice(1)).pipe(
            map((slice) => ({ slice, data: value.data }))
          );
        }),
        delay(200),
        concatMap(({ slice, data }) => {
          this.filteredNodes.push(...data.slice(slice.start, slice.end));
          return of({ slice, data });
        }),
        takeUntil(this.unsubscribe)
      )
      .subscribe({
        next: ({ slice, data }) => {
          if (slice.end === data.length) {
            this.initCustomTableStyles();
            this.isTableLoading = false;
          }
        },
      });
  }

  protected onInputChange(searchInput: string): void {
    this.searchSubject.next(searchInput);
  }

  private listenToSearchSubject(): void {
    this.searchSubject
      .pipe(debounceTime(300), takeUntil(this.unsubscribe))
      .subscribe(this.searchSubjectSubscribeFn);
  }

  private readonly searchSubjectSubscribeFn = (searchInput: string) => {
    if (searchInput !== undefined && searchInput !== null) {
      this.dispatchBatchProcess(this.filterNodesFn(searchInput.trim()));
      this.initCustomTableStyles();
    }
  };

  private readonly filterNodesFn = (
    searchInput: string
  ): DnaSyngoviaSelectionType[] => {
    let result: DnaSyngoviaSelectionType[] = [];

    if (this.groupNode.nodes?.length > 0) {
      result = this.selectionModel.selection.filter(
        (element: DnaSyngoviaSelectionType) => {
          const columns =
            this.columnsDictionary[this.groupNode.type] ??
            this.columnsDictionary.default;

          let searchKey = '';
          columns.forEach((column) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let value = (element.value as any)[column.property];
            if ('date' === column.columnType) {
              value = this.localizedDatePipe.transform(value);
            }

            if (value) {
              searchKey += value + ' ';
            }
          });

          return searchKey?.toLowerCase().includes(searchInput?.toLowerCase());
        },
        this
      );
    }

    return result;
  };

  protected sortByProperty(property: string, isDescending = false): void {
    this.selectionModel.selection.sort(
      this.defaultSortFn(property, isDescending)
    );
    if (this.searchInput) {
      const inputValue = this.searchInput.nativeElement.value;
      const selectionLength = this.selectionModel.selection.length;
      if (
        inputValue?.trim() === '' &&
        this.filteredNodes.length === selectionLength
      ) {
        this.filteredNodes = this.selectionModel.selection;
      } else {
        this.dispatchBatchProcess(this.filterNodesFn(inputValue));
      }
    }
  }

  private readonly defaultSortFn =
    (property: string, isDescending: boolean) =>
    (
      nodeA: DnaSyngoviaSelectionType,
      nodeB: DnaSyngoviaSelectionType
    ): number => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const valueA = ((nodeA.value as any)[property] as string) ?? '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const valueB = ((nodeB.value as any)[property] as string) ?? '';

      const result = valueA.localeCompare(valueB);

      return isDescending ? -1 * result : result;
    };

  protected onCompareClick(): void {
    this.modalVisible = true;
  }

  protected onCloseCompareModalClick(): void {
    this.modalVisible = false;
  }

  protected onHeaderCheckboxClick(checked: boolean): void {
    this.selectionModel.toggleAll(checked);
  }

  protected onRowCheckboxClick(element: DnaSyngoviaSelectionType): void {
    this.selectionModel.toggleSelection(element);
  }

  private applyTableStyles(selector: string, style: string): void {
    const styleSheet = new CSSStyleSheet();
    const hosts = document.querySelectorAll(selector);

    styleSheet.replaceSync(style);

    hosts.forEach((host) => {
      host?.shadowRoot?.adoptedStyleSheets.push(styleSheet);
    });
  }

  private initCustomTableStyles(): void {
    setTimeout(() => {
      this.applyTableStyles(
        'sh-table-row.chapter-sibling.nested',
        `div#wrapper > sh-icon#tableExpandIcon {
			  left: calc(var(--sh-table-row-initial-icon-left) + var(--nesting-level) * var(--sh-table-row-multiselect-checkbox-left) + 1.5rem);
		  }`
      );

      this.applyTableStyles(
        'sh-table-row.chapter-sibling.nested sh-table-cell.checkbox-cell',
        `div.cell-wrapper {
				margin-right: 0;
				flex-direction: row;
			}`
      );

      this.applyTableStyles(
        'sh-table-head[checkbox]',
        `div.head-wrapper {
			  margin-left: 0.25rem;
		  }`
      );

      this.applyTableStyles(
        'sh-table-row.nested.syngovia-nested',
        `div#wrapper > sh-icon#tableExpandIcon {
			  left: calc(var(--sh-table-row-initial-icon-left) + var(--nesting-level) * var(--sh-table-row-multiselect-checkbox-left) + 1.5rem);
		  }`
      );
    });
  }

  protected onServerWarningClick(node: DnaSyngoviaNodeType): void {
    if (DnaSyngoviaNodeEType.SERVER === node.type) {
      this.screenInteractionService.serverHealthcheckNext(node);
    }
  }
}
